import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { ConfigProvider, Layout } from 'antd';

import { auth } from 'lib/api/firebase';
import { AuthContext, useReduxAuthStore } from 'lib/hooks/auth/use-auth';

import { RouteSwitch } from 'components';
import { restoreAuth, User } from 'features/auth/authSlice';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorDisplay } from 'components/ErrorDisplay';
import { Role } from 'features/auth/types';
import {
  omitSideMenuRoutes,
  omitSideMenuRoutesPatient
} from 'lib/constants/filters';
import { ErrorBoundary } from 'react-error-boundary';
import { useLocation } from 'react-router-dom';
import { NavigationMenu } from 'navigation/menu/menu';

import dayjs from 'dayjs';

/**
 * This code is required in order for dayjs to work with AntD DatePicker
 *
 * This is bad code.
 *
 * TODO: Migrate away from AntD
 */
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);

export default function App() {
  const location = useLocation().pathname;
  const dispatch = useDispatch();

  const authState = useReduxAuthStore();

  useEffect(() => {
    dispatch(restoreAuth(auth));
  }, [dispatch]);

  /**
   * Navigation should be rendered when:
   * - The user is logged in
   * - The current route is not in the ommitted routes list
   * - **(2024-01-29) The user is not a patient. Patients will not see the side menu.
   */
  const shouldRenderNavigation = resolveRenderNavigation(
    authState.user,
    location
  );

  /**
   * Cache the instance. If we don't do this then the cache will be cleared anytime App.tsx is rerenderd
   * E.g: Route navigation
   */
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnReconnect: false,
            refetchOnWindowFocus: false
          }
        }
      }),
    []
  );
  return (
    <AuthContext value={authState}>
      <ConfigProvider
        /**
         * global theme override css
         * https://ant.design/docs/react/customize-theme
         *
         * During testing the variables in src/styles/theme.less and src/styles/palette.scss don't
         * impact the styles AntD uses
         */
        theme={{
          token: {
            colorPrimary: '#230056',
            colorPrimaryHover: '#A366FC',
            fontFamily: 'Comfortaa',
            colorTextPlaceholder: '#767679'
          },
          components: {
            Card: {
              headerFontSize: `${26 / 16}rem` as any
            },
            Button: {
              defaultColor: 'black'
            },
            Switch: {
              handleShadow: '0'
            }
          },
          cssVar: true
        }}
      >
        <Layout>
          <QueryClientProvider client={queryClient}>
            <ErrorBoundary
              fallbackRender={props => (
                <ErrorDisplay error={props.error} description="" title="" />
              )}
            >
              {shouldRenderNavigation && <NavigationMenu />}
            </ErrorBoundary>
            <RouteSwitch />
          </QueryClientProvider>
        </Layout>
      </ConfigProvider>
    </AuthContext>
  );
}

function resolveRenderNavigation(user: User | null, location: string) {
  if (!user) return false;
  /**
   * Case 1: The user is not a patient
   */
  if (user.role !== Role.Patient) {
    return ![...omitSideMenuRoutes].find(match =>
      location.toLowerCase().includes(match.toLowerCase())
    );
  }
  /**
   * Case 2: The user is a patient
   */
  return !omitSideMenuRoutesPatient.find(match =>
    location.toLowerCase().includes(match.toLowerCase())
  );
}
